import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const PT_TO_MM = 25.4 / 72;
const PIXEL_PROPS = [
  'left', 'top', 'right', 'bottom', 'width', 'height',
  'minWidth', 'minHeight', 'maxWidth', 'maxHeight',
  'fontSize', 'lineHeight', 'letterSpacing',
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
  'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth'
];

function pagePoints(element) {
  const widthPt = Number(element?.dataset?.pdfPageWidth);
  const heightPt = Number(element?.dataset?.pdfPageHeight);
  if (Number.isFinite(widthPt) && widthPt > 0 && Number.isFinite(heightPt) && heightPt > 0) {
    return { widthPt, heightPt };
  }
  return null;
}

function physicalPage(element) {
  const points = pagePoints(element);
  if (points) return points;
  const widthPx = element?.getBoundingClientRect?.().width || element?.offsetWidth || 0;
  const heightPx = element?.getBoundingClientRect?.().height || element?.offsetHeight || 0;
  return { widthPt: widthPx * 72 / 96, heightPt: heightPx * 72 / 96 };
}

function parsePx(value) {
  if (typeof value !== 'string' || !value.endsWith('px')) return null;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function scaleInlinePixels(style, factor) {
  for (const prop of PIXEL_PROPS) {
    const value = style[prop];
    const px = parsePx(value);
    if (px !== null) style[prop] = `${px * factor}px`;
  }
}

function prepareExportClone(element, widthPt, heightPt) {
  const sourceRect = element.getBoundingClientRect();
  const scale = sourceRect.width / widthPt;
  const safeScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
  const factor = 1 / safeScale;
  const clone = element.cloneNode(true);

  clone.style.position = 'relative';
  clone.style.left = '0px';
  clone.style.top = '0px';
  clone.style.width = `${widthPt}px`;
  clone.style.height = `${heightPt}px`;
  clone.style.margin = '0';
  clone.style.transform = 'none';
  clone.style.boxShadow = 'none';
  clone.style.overflow = 'hidden';

  const descendants = [...clone.querySelectorAll('*')];
  descendants.forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    scaleInlinePixels(node.style, factor);
    node.style.transform = 'none';
    node.style.boxShadow = 'none';
    node.removeAttribute('title');
    node.removeAttribute('contenteditable');
    node.blur?.();
    if (node.matches?.('[data-pdf-editor-field]')) {
      node.style.caretColor = 'transparent';
      node.style.userSelect = 'none';
      node.style.pointerEvents = 'none';
    }
  });

  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-100000px';
  host.style.top = '0';
  host.style.width = `${widthPt}px`;
  host.style.height = `${heightPt}px`;
  host.style.margin = '0';
  host.style.padding = '0';
  host.style.overflow = 'hidden';
  host.style.pointerEvents = 'none';
  host.style.background = '#fff';
  host.appendChild(clone);
  document.body.appendChild(host);

  return { host, clone, scale: safeScale };
}

async function capturePage(element) {
  const dims = physicalPage(element);
  const prepared = prepareExportClone(element, dims.widthPt, dims.heightPt);

  try {
    if (document.fonts?.ready) await document.fonts.ready;
    return await html2canvas(prepared.clone, {
      scale: 3,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      logging: false,
      width: Math.ceil(dims.widthPt),
      height: Math.ceil(dims.heightPt),
      scrollX: 0,
      scrollY: 0,
      removeContainer: true,
      onclone: (doc) => {
        const style = doc.createElement('style');
        style.textContent = `
          [data-pdf-editor-field], .editable-text, .invoice-editable-line {
            outline: none !important;
            border: 0 !important;
            box-shadow: none !important;
            background: transparent !important;
            caret-color: transparent !important;
            user-select: none !important;
            cursor: default !important;
          }
        `;
        doc.head.appendChild(style);
      }
    });
  } finally {
    prepared.host.remove();
  }
}

const PDFExportButton = ({ previewRef, fileName = 'document.pdf' }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);

  const handleExport = async () => {
    if (!previewRef?.current) {
      setError('Aucun aperçu disponible.');
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      if (document.fonts?.ready) await document.fonts.ready;

      const receipt = previewRef.current.querySelector('.receipt-canvas');
      const invoicePages = Array.from(previewRef.current.querySelectorAll('.invoice-page'));
      const pages = receipt ? [receipt] : invoicePages;
      if (!pages.length) throw new Error('Aucun aperçu disponible.');

      const first = physicalPage(pages[0]);
      const pdf = new jsPDF({
        orientation: first.widthPt > first.heightPt ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [first.widthPt * PT_TO_MM, first.heightPt * PT_TO_MM],
        compress: true,
      });

      for (let index = 0; index < pages.length; index += 1) {
        if (index > 0) {
          const dims = physicalPage(pages[index]);
          pdf.addPage(
            [dims.widthPt * PT_TO_MM, dims.heightPt * PT_TO_MM],
            dims.widthPt > dims.heightPt ? 'landscape' : 'portrait'
          );
        }

        const element = pages[index];
        const dims = physicalPage(element);
        const canvas = await capturePage(element);
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0,
          0,
          dims.widthPt * PT_TO_MM,
          dims.heightPt * PT_TO_MM,
          undefined,
          'FAST'
        );
      }

      pdf.save(fileName);
    } catch (err) {
      console.error('PDF export error:', err);
      setError(`Erreur lors de l’export : ${err?.message || 'Erreur inconnue.'}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="export-wrap">
      <button className="export-button" type="button" onClick={handleExport} disabled={isExporting}>
        {isExporting ? 'Exportation…' : '📄 Télécharger le PDF'}
      </button>
      {error && <p className="export-error">{error}</p>}
    </div>
  );
};

export default PDFExportButton;
